import { useEffect, useId, useRef, useState } from 'react';
import { tr, useCurrentLocale } from '../i18n';
import { ShieldIcon } from './icons';
import { resolveCheckoutErrorMessages, type CheckoutErrorDescriptor } from './errorPresentation';

export interface CheckoutErrorAlertProps {
  error: CheckoutErrorDescriptor;
  placement?: 'inline' | 'page' | 'toast' | 'modal';
  onAction?: () => void;
  onDismiss?: () => void;
  autoFocus?: boolean;
  id?: string;
}

const TOAST_DISMISS_MS = 6_000;

export function CheckoutErrorAlert({
  error,
  placement = 'inline',
  onAction,
  onDismiss,
  autoFocus = false,
  id,
}: CheckoutErrorAlertProps) {
  useCurrentLocale();
  const alertRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dialogId = useId();
  const onDismissRef = useRef(onDismiss);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (autoFocus && placement !== 'modal') {
      alertRef.current?.focus();
    }
  }, [autoFocus, placement]);

  useEffect(() => {
    if (placement !== 'modal') return;
    const dialog = dialogRef.current!;
    const overflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    dialog.querySelector<HTMLButtonElement>('button')?.focus();
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
    };
  }, [placement]);

  useEffect(() => {
    setVisible(true);
    if (placement !== 'toast') {
      return;
    }
    const timeout = window.setTimeout(() => {
      setVisible(false);
      onDismissRef.current?.();
    }, TOAST_DISMISS_MS);
    return () => window.clearTimeout(timeout);
  }, [error, placement]);

  const actionLabel = error.action === 'refresh' ? tr('error.refresh') : tr('error.retry');
  const { title, message } = resolveCheckoutErrorMessages(error);
  const messageId = id ?? (placement === 'modal' ? `${dialogId}-message` : error.field === undefined ? undefined : `checkout-error-${error.field}`);

  if (!visible) {
    return null;
  }

  const alert = (
    <div
      ref={alertRef}
      className={`vp-error-alert vp-error-alert--${placement} vp-error-alert--${error.kind}`}
      role="alert"
      aria-live="assertive"
      tabIndex={-1}
    >
      <div className="vp-error-alert__icon" aria-hidden="true">
        <ShieldIcon width={22} height={22} />
      </div>
      <div className="vp-error-alert__content">
        <h2 className="vp-error-alert__title" id={placement === 'modal' ? `${dialogId}-title` : undefined}>{title}</h2>
        <p className="vp-error-alert__message" id={messageId}>{message}</p>
        {error.action !== undefined && onAction !== undefined && (
          <button className="vp-error-alert__action" type="button" onClick={onAction}>
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );

  return placement === 'modal' ? (
    <dialog
      ref={dialogRef}
      className="vp-error-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={`${dialogId}-title`}
      aria-describedby={messageId}
      onCancel={event => event.preventDefault()}
    >
      {alert}
    </dialog>
  ) : alert;
}
